import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      setMessage("Thank you for subscribing!");
      setEmail("");
      return;
    }
    if (!email.trim()) return;

    setStatus("loading");
    try {
      const body = new URLSearchParams();
      body.append("email", email);
      await fetch("https://readdy.ai/api/form/d8uionhl2p03kijan38g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setStatus("success");
      setMessage("Thank you for subscribing!");
      setEmail("");
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <footer className="bg-primary-500 text-background-50">
      <div className="px-4 md:px-6 py-16 md:py-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-16">
            {/* Column 1 */}
            <div>
              <h3 className="font-heading text-2xl font-semibold mb-6">Get The Best Sheet</h3>
              <p className="text-sm text-background-50/70 leading-relaxed mb-6">
                Join thousands of smart shoppers who use the AllChinaBuy Spreadsheet to discover quality finds every day.
              </p>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-background-50/30 rounded-full text-sm text-background-50 hover:bg-background-50 hover:text-primary-500 transition-all whitespace-nowrap cursor-pointer"
              >
                Explore Spreadsheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>

            {/* Column 2 - Navigation */}
            <div>
              <h4 className="font-heading text-base font-semibold mb-5">Navigation</h4>
              <div className="flex flex-col gap-3">
                <Link to="/" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap">Home</Link>
                <Link to="/#categories" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap">Categories</Link>
                <Link to="/guide/allchinabuy-sheet-complete-guide" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap">Beginner Guide</Link>
                <Link to="/#faq" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap">FAQ</Link>
                <Link to="/#safety" className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap">Safety Notice</Link>
              </div>
            </div>

            {/* Column 3 - Quick Categories */}
            <div>
              <h4 className="font-heading text-base font-semibold mb-5">Popular Categories</h4>
              <div className="flex flex-col gap-2">
                {categories.slice(0, 7).map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-background-50/70 hover:text-background-50 transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>

            {/* Column 4 - Newsletter */}
            <div>
              <h4 className="font-heading text-base font-semibold mb-3">Stay Updated</h4>
              <p className="text-sm text-background-50/70 mb-5 leading-relaxed">
                Get notified when new finds are added to the spreadsheet.
              </p>
              <form onSubmit={handleSubmit} data-readdy-form className="flex flex-col gap-3">
                {/* Honeypot */}
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" className="ab-fld-aux" />
                <div className="flex">
                  <input
                    type="email"
                    name="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="flex-1 px-4 py-2.5 bg-transparent border border-background-50/25 rounded-l-full text-sm text-background-50 placeholder:text-background-50/40 outline-none focus:border-background-50/50"
                  />
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="px-5 py-2.5 bg-background-50 text-primary-500 rounded-r-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {status === "loading" ? (
                      <span className="flex items-center gap-1">
                        <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      </span>
                    ) : (
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    )}
                  </button>
                </div>
                {status === "success" && <p className="text-xs text-green-400">{message}</p>}
                {status === "error" && <p className="text-xs text-red-400">{message}</p>}
              </form>
            </div>
          </div>

          {/* Bottom decoration + copyright */}
          <div className="mt-16 pt-8 border-t border-background-50/10 relative">
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none">
              <span className="font-heading text-[120px] md:text-[160px] font-bold text-background-50/[0.03] whitespace-nowrap leading-none">
                ALLCHINABUY
              </span>
            </div>
            <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-background-50/50">
                &copy; 2026 AllChinaBuy Spreadsheet Directory. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-background-50/50 hover:text-background-50/80 transition-colors whitespace-nowrap">Main Website</a>
                <a href="#" className="text-xs text-background-50/50 hover:text-background-50/80 transition-colors whitespace-nowrap">Privacy Policy</a>
                <a href="#" className="text-xs text-background-50/50 hover:text-background-50/80 transition-colors whitespace-nowrap">Terms</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}